// Movement decisions read held JSON and accumulate only changed branches. No before-image,
// serialization or history: discarding a decision simply discards its writes.
import type { JanusCtx, Row } from '../ability/context.js';

type JsonObject = Record<PropertyKey, unknown>;
interface Node {
  source: JsonObject; copy?: JsonObject; proxy: JsonObject;
  children: Map<PropertyKey, Node>; parents: Map<Node, Set<PropertyKey>>;
  facts?: Map<PropertyKey, PropertyDescriptor>; pin?: () => void;
  result?: JsonObject;
}
export interface DecisionCounts { frames: number; branchCopies: number; memberPatches: number }
// A context may be handed to another engine bundle. The handles belong to the view,
// rather than a bundle-local registry that the receiving engine would duplicate.
const NODE = Symbol.for('fatetide.movementDecision.node');
const FINISH = Symbol.for('fatetide.movementDecision.finish');
type DecisionMembers = JanusCtx['members'] & { [FINISH]?: () => Row };
const nodeOf = (value: object): Node | undefined => (value as { [NODE]?: Node })[NODE];
const object = (v: unknown): v is JsonObject => {
  if (v === null || typeof v !== 'object') return false;
  const prototype = Object.getPrototypeOf(v) as object | null;
  // Held inputs may come from the page/iframe that owns another engine bundle.
  return Array.isArray(v) || prototype === null || Object.getPrototypeOf(prototype) === null;
};
const publish = (counts: DecisionCounts): void => {
  (globalThis as { ftMovementDecisionCount?: (counts: DecisionCounts) => void }).ftMovementDecisionCount?.(counts);
};
const owns = (value: object, key: PropertyKey): boolean => Object.prototype.hasOwnProperty.call(value, key);
function shallow(source: JsonObject): JsonObject {
  if (Array.isArray(source)) return source.slice() as unknown as JsonObject;
  const out = { ...source };
  const base = Object.getOwnPropertyDescriptor(source, '_entity_base');
  if (base && !base.enumerable) Object.defineProperty(out, '_entity_base', base);
  return out;
}

/** Pin one selected mutable fact container before an asynchronous boundary.
 * Catalogs and unrelated branches are borrowed; callers select the turn facts. */
export function pinDecisionFacts(value: unknown): void {
  if (value && typeof value === 'object') nodeOf(value)?.pin?.();
}

function read(node: Node, key: PropertyKey): unknown {
  if (node.copy) return node.copy[key];
  if (!node.facts) return node.source[key];
  if (node.facts.has(key)) return node.facts.get(key)!.value;
  const prototype = Object.getPrototypeOf(node.source) as JsonObject | null;
  return prototype?.[key];
}

function mutable(node: Node): JsonObject {
  if (!node.facts) return shallow(node.source);
  const out = (Array.isArray(node.source) ? [] : Object.create(Object.getPrototypeOf(node.source))) as JsonObject;
  for (const [key, descriptor] of node.facts) Object.defineProperty(out, key, descriptor);
  return out;
}

function result(node: Node): JsonObject {
  if (!node.copy) return node.source;
  if (node.result) return node.result;
  const out = node.result = shallow(node.copy);
  publish({ frames: 0, branchCopies: 1, memberPatches: 0 });
  for (const [key, child] of node.children) if (owns(out, key)) out[key] = result(child);
  return out;
}

/** Borrow one already-held decision input without walking its untouched descendants. */
export function decisionRead<T>(value: T): T {
  const node = value && typeof value === 'object' ? nodeOf(value) : undefined;
  return node ? result(node) as T : value;
}

/** Strip decision handles from the small write payload, retaining every untouched value. */
export function decisionWrites<T>(value: T): T {
  if (!value || typeof value !== 'object') return value;
  const node = nodeOf(value);
  if (node) return result(node) as T;
  if (!object(value)) return value;
  let out: JsonObject | undefined;
  for (const key of Object.keys(value)) {
    const before = value[key], after = decisionWrites(before);
    if (after !== before) {
      out ??= Array.isArray(value) ? value.slice() as unknown as JsonObject : { ...value };
      out[key] = after;
    }
  }
  return (out ?? value) as T;
}

/** A read view with a named finish operation. Reads allocate handles, never copies;
 * the first actual write copies its branch and parents, sharing catalogs/geometry/other members.
 * A caller crossing a yield must first pin its mutable fact containers.
 * Finish before retaining a chain state or forking a face; never mutate a finished view. */
export function movementDecisionView(encounter: Row, held: ReadonlyMap<string, Row>):
  Pick<JanusCtx, 'encounter' | 'members' | 'originals'> {
  const memo = new WeakMap<object, Node>();
  const roots = new Set<Node>();
  const pinned = new Set<Node>();
  const invalidate = (node: Node): void => {
    node.result = undefined;
    for (const parent of node.parents.keys()) invalidate(parent);
  };
  const change = (node: Node): void => {
    if (node.copy) return;
    node.copy = mutable(node);
    publish({ frames: 0, branchCopies: 1, memberPatches: roots.has(node) ? 1 : 0 });
    for (const parent of node.parents.keys()) change(parent);
  };
  const wrap = (source: JsonObject): Node => {
    const known = memo.get(source);
    if (known) return known;
    const node: Node = { source, proxy: {}, children: new Map(), parents: new Map() };
    node.pin = () => {
      if (node.facts) return;
      node.facts = new Map(Reflect.ownKeys(source).map(key => [key, {
        value: source[key], enumerable: Object.getOwnPropertyDescriptor(source, key)!.enumerable,
        configurable: key !== 'length' || !Array.isArray(source), writable: true,
      } as PropertyDescriptor]));
      pinned.add(node);
    };
    memo.set(source, node);
    const target = Array.isArray(source) ? [] : {};
    node.proxy = new Proxy(target, {
      get(_target, key) {
        if (key === NODE) return node;
        const value = read(node, key);
        if (!object(value)) return value;
        const own = nodeOf(value);
        // An input from another completed decision belongs to this decision from here on.
        const child = wrap(own ? result(own) : value);
        node.children.set(key, child);
        let keys = child.parents.get(node);
        if (!keys) child.parents.set(node, keys = new Set());
        keys.add(key);
        if (child.copy) change(node);
        return child.proxy;
      },
      set(_target, key, value) {
        const current = read(node, key);
        if (Object.is(current, value) || node.children.get(key)?.proxy === value) return true;
        const plain = decisionWrites(value);
        // Filtering an unchanged status bucket is not a changed branch.
        if (Array.isArray(plain) && Array.isArray(current) && plain.length === current.length
          && plain.every((entry, i) => entry === decisionRead(current[i]))) return true;
        change(node);
        invalidate(node);
        detach(node, key);
        node.children.delete(key);
        node.copy![key] = plain;
        return true;
      },
      defineProperty(_target, key, descriptor) {
        return 'value' in descriptor ? Reflect.set(node.proxy, key, descriptor.value) : false;
      },
      deleteProperty(_target, key) {
        if (!(node.copy ? owns(node.copy, key) : node.facts ? node.facts.has(key) : owns(source, key))) return true;
        change(node); invalidate(node); detach(node, key); node.children.delete(key); delete node.copy![key]; return true;
      },
      has: (_target, key) => node.copy ? key in node.copy : node.facts ? node.facts.has(key) || key in (Object.getPrototypeOf(source) ?? {}) : key in source,
      ownKeys: () => node.copy ? Reflect.ownKeys(node.copy) : node.facts ? [...node.facts.keys()] as (string | symbol)[] : Reflect.ownKeys(source),
      getOwnPropertyDescriptor(_target, key) {
        const descriptor = node.copy ? Object.getOwnPropertyDescriptor(node.copy, key)
          : node.facts ? node.facts.get(key) : Object.getOwnPropertyDescriptor(source, key);
        return descriptor ? { ...descriptor, configurable: key !== 'length' || !Array.isArray(source) } : undefined;
      },
    }) as JsonObject;
    return node;
  };
  const detach = (parent: Node, key: PropertyKey): void => {
    const child = parent.children.get(key), keys = child?.parents.get(parent);
    keys?.delete(key);
    if (child && keys?.size === 0) child.parents.delete(parent);
  };
  const members = new Map<string, Row>(), originals = new Map<string, Row>();
  for (const [id, member] of held) {
    const own = nodeOf(member), source = own ? result(own) as Row : member;
    const node = wrap(source); roots.add(node);
    members.set(id.toUpperCase(), node.proxy as Row); originals.set(id.toUpperCase(), source);
  }
  const ownScene = nodeOf(encounter);
  const scene = wrap(ownScene ? result(ownScene) : encounter);
  Object.defineProperty(members, FINISH, { configurable: true, value: () => {
    // A later page revision may have replaced a pinned fact in place. Materialize
    // only that old fact's parent path so terminal readers keep this attempt's epoch.
    for (const node of pinned) if (!node.copy && (Reflect.ownKeys(node.source).length !== node.facts!.size
      || [...node.facts!].some(([key, descriptor]) => !Object.is(node.source[key], descriptor.value)))) {
      change(node); invalidate(node);
    }
    for (const [id, member] of members) members.set(id, decisionWrites(member));
    return result(scene) as Row;
  } });
  publish({ frames: 1, branchCopies: 0, memberPatches: 0 });
  return { encounter: scene.proxy as Row, members, originals };
}

/** Publish this decision's changed member branches; unchanged members keep their held identity. */
export function finishMovementDecision(ctx: Pick<JanusCtx, 'encounter' | 'members'>): void {
  const finish = (ctx.members as DecisionMembers)[FINISH];
  if (!finish) return;
  ctx.encounter = finish();
  delete (ctx.members as DecisionMembers)[FINISH];
}
